import React, { useCallback, useEffect, useRef } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from 'lucide-react';
import { ImageSourceBadge } from '../ui/ImageSourceBadge';
import { useFocusTrap } from '../../hooks/useFocusTrap';
import { EASE_OUT } from '../../utils/motion';
import type { SiteImage } from '../../types/content';
interface ProjectLightboxProps {
  images: SiteImage[];
  index: number;
  title: string;
  onClose: () => void;
  onNavigate: (index: number) => void;
}
export function ProjectLightbox({
  images,
  index,
  title,
  onClose,
  onNavigate
}: ProjectLightboxProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const count = images.length;
  const image = images[index];
  useFocusTrap(containerRef, true);
  const go = useCallback((delta: number) => onNavigate((index + delta + count) % count), [index, count, onNavigate]);
  useEffect(() => {
    closeRef.current?.focus();
  }, []);
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
      } else if (event.key === 'ArrowRight' && count > 1) {
        go(1);
      } else if (event.key === 'ArrowLeft' && count > 1) {
        go(-1);
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [go, count, onClose]);
  return <motion.div ref={containerRef} role="dialog" aria-modal="true" aria-label={`${title}: image ${index + 1} of ${count}`} initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: EASE_OUT
  }} className="fixed inset-0 z-[70] flex flex-col bg-black/95" data-id="element-411" data-mp="39e4grtgtjp59">
      <div className="flex items-center justify-between px-5 py-4 md:px-8" data-id="element-412" data-mp="2qfic64m3ffwl">
        <p className="font-display text-[15px] font-semibold uppercase tracking-[0.14em] text-steel-300" data-id="element-413" data-mp="2qga517ob2zj6">
          {index + 1} / {count}
        </p>
        <button ref={closeRef} type="button" onClick={onClose} className="flex h-11 w-11 items-center justify-center text-white transition-colors duration-150 hover:text-brand-light" aria-label="Close image viewer" data-id="element-414" data-mp="2lr96idi96q3e">
          <XIcon className="h-6 w-6" aria-hidden="true" data-id="element-415" data-mp="2zidwenvtk40j" />
        </button>
      </div>

      <div className="relative flex min-h-0 flex-1 items-center justify-center px-4 md:px-20" data-id="element-416" data-mp="2zkqf539eiien">
        <AnimatePresence mode="wait" initial={false} data-id="element-417" data-mp="34jbfd2onl233">
          <motion.img key={`${image.src}-${index}`} src={image.src} alt={image.alt} initial={{
          opacity: 0,
          scale: 0.98
        }} animate={{
          opacity: 1,
          scale: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.2,
          ease: EASE_OUT
        }} className="max-h-full max-w-full object-contain" data-id="element-418" data-mp="2prje1ulp1okd" />
        </AnimatePresence>
        {count > 1 && <>
            <button type="button" onClick={() => go(-1)} className="absolute left-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-charcoal-900/80 text-white transition-colors duration-150 hover:bg-brand-action md:left-6" aria-label="Previous image" data-id="element-419" data-mp="1bqmxnm22ca1r">
              <ChevronLeftIcon className="h-6 w-6" aria-hidden="true" data-id="element-420" data-mp="sv6kvk7cpgsi" />
            </button>
            <button type="button" onClick={() => go(1)} className="absolute right-2 top-1/2 flex h-12 w-12 -translate-y-1/2 items-center justify-center bg-charcoal-900/80 text-white transition-colors duration-150 hover:bg-brand-action md:right-6" aria-label="Next image" data-id="element-421" data-mp="nvu158bs0pu5">
              <ChevronRightIcon className="h-6 w-6" aria-hidden="true" data-id="element-422" data-mp="2d85mm6wmsn2z" />
            </button>
          </>}
      </div>

      <div className="mx-auto flex w-full max-w-4xl flex-col items-center gap-2 px-5 py-5 text-center" data-id="element-423" data-mp="34iju7tpec2xe">
        <ImageSourceBadge source={image.source} data-id="element-424" data-mp="38jvk80c6mq64" />
        {image.caption && <p className="text-[15px] text-steel-100" data-id="element-425" data-mp="38m7zu6ugwog0">{image.caption}</p>}
      </div>
    </motion.div>;
}